import axios from 'axios';

import adminAxiosAPI from './adminAxiosAPI';
import axiosAPI from './axiosAPI';

import { apiAddress } from '../config';
import { AgroModel } from '../models/AgroModel';
import { Company } from '../models/Company';
import { Field } from '../models/Field';
import { FieldCropHistory, FieldCropHistoryInput } from '../models/FieldCropHistory';
import { LocationRegion } from '../models/LocationRegion';
import { User } from '../models/User';
import { WikiPage, WikiSection } from '../models/Wiki';
import { Zond } from '../models/Zond';
import { ZondNotification } from '../models/ZondNotification';

export class ResponseOK {
    status: boolean = true;
    message: string = '';
}

export class API {
    LoginUser(username: string, password: string): Promise<{ token: string; is_admin: boolean }> {
        return new Promise((resolve, reject) => {
            axios
                .post(`${apiAddress}/login`, {
                    username: username,
                    password: password,
                })
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    GetUser(): Promise<User> {
        return new Promise((resolve, reject) => {
            axiosAPI
                .get(`/user`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    GetCompany(): Promise<Company> {
        return new Promise((resolve, reject) => {
            axiosAPI
                .get(`/company`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    GetCompanies(): Promise<Company[]> {
        return new Promise((resolve, reject) => {
            axiosAPI
                .get(`/all_companies`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    LocationRegions(): Promise<LocationRegion[]> {
        return new Promise((resolve, reject) => {
            adminAxiosAPI
                .get(`/location_region?_start=0&_end=100`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    GetZonds(): Promise<Zond[]> {
        return new Promise((resolve, reject) => {
            axiosAPI
                .get(`/zonds`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    UpdateZondSoil(id: number, data: Partial<Zond>): Promise<{ status: boolean; message: string }> {
        return new Promise((resolve, reject) => {
            axiosAPI
                .put(`/zonds/${id}/soil`, data)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    GetFieldOfCompany(): Promise<Field> {
        return new Promise((resolve, reject) => {
            axiosAPI
                .get(`/field_of_company`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    GetFields(): Promise<Field[]> {
        return new Promise((resolve, reject) => {
            axiosAPI
                .get(`/fields`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    CreateField(data: Partial<Field>): Promise<Field> {
        return new Promise((resolve, reject) => {
            axiosAPI
                .post(`/fields`, data)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    UpdateField(id: number, data: Partial<Field>): Promise<Field> {
        return new Promise((resolve, reject) => {
            axiosAPI
                .put(`/fields/${id}`, data)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    DeleteField(id: number): Promise<{ status: boolean; message: string }> {
        return new Promise((resolve, reject) => {
            axiosAPI
                .delete(`/fields/${id}`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    GetFieldSeasons(fieldId: number): Promise<FieldCropHistory[]> {
        return new Promise((resolve, reject) => {
            axiosAPI
                .get(`/fields/${fieldId}/seasons`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    GetFieldSeason(fieldId: number, year: number): Promise<FieldCropHistory> {
        return new Promise((resolve, reject) => {
            axiosAPI
                .get(`/fields/${fieldId}/seasons/${year}`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    UpsertFieldSeason(
        fieldId: number,
        year: number,
        data: FieldCropHistoryInput,
    ): Promise<FieldCropHistory> {
        return new Promise((resolve, reject) => {
            axiosAPI
                .put(`/fields/${fieldId}/seasons/${year}`, data)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    DeleteFieldSeason(fieldId: number, year: number): Promise<{ status: boolean; message: string }> {
        return new Promise((resolve, reject) => {
            axiosAPI
                .delete(`/fields/${fieldId}/seasons/${year}`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    ImportField(file: File, name?: string, location?: string): Promise<Field[]> {
        const formData = new FormData();
        formData.append('file', file);
        if (name) formData.append('name', name);
        if (location) formData.append('location', location);
        return new Promise((resolve, reject) => {
            axiosAPI
                .post('/fields/import', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                })
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    GetWikiSection(id = 1): Promise<WikiSection> {
        return new Promise((resolve, reject) => {
            axiosAPI
                .get(`/wiki_section/${id}`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    GetWikiPage(id = 0): Promise<WikiPage> {
        return new Promise((resolve, reject) => {
            axiosAPI
                .get(`/wiki_page/${id}`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    GetAgroModels(): Promise<AgroModel[]> {
        return new Promise((resolve, reject) => {
            axiosAPI
                .get(`/agro_models`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    GetZondNotifications(start = 0, end = 25): Promise<ZondNotification[]> {
        return new Promise((resolve, reject) => {
            axiosAPI
                .get(`/zond_notifications?_start=${start}&_end=${end}`)
                .then((response) => {
                    let res = [];
                    for (let n of response.data) {
                        n['total_count'] = parseInt(response.headers['x-total-count']);
                        res.push(n);
                    }
                    resolve(res);
                })
                .catch((error) => reject(error));
        });
    }

    CheckZondNotification(id: number): Promise<ResponseOK> {
        return new Promise((resolve, reject) => {
            axiosAPI
                .post(`/check_notification/${id}`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    EditUser(email: string, name?: string, surname?: string, patronymic?: string, phone?: string): Promise<ResponseOK> {
        return new Promise((resolve, reject) => {
            axiosAPI
                .post(`/edit_user`, {
                    name: name,
                    surname: surname,
                    patronymic: patronymic,
                    email: email,
                    phone_number: phone,
                })
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    EditCompany(payload: {
        name: string;
        description?: string;
        address?: string;
        phone?: string;
        company_type?: string;
        inn?: string;
        kpp?: string;
        bank_name?: string;
        account?: string;
        corr_account?: string;
        bik?: string;
        legal_address?: string;
        postal_address?: string;
        accounting_email?: string;
        contact_name?: string;
        contact_email?: string;
    }): Promise<ResponseOK> {
        return new Promise((resolve, reject) => {
            axiosAPI
                .post(`/edit_company`, payload)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    ReportDeviceIssue(numberInDB: string, culture: string, startDate: string, endDate: string): Promise<ResponseOK> {
        return new Promise((resolve, reject) => {
            axiosAPI
                .post(`/report_device_issue`, {
                    number_in_db: numberInDB,
                    culture: culture,
                    start_date: startDate,
                    end_date: endDate,
                })
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    ChangePassword(password: string, newPassword: string): Promise<ResponseOK> {
        return new Promise((resolve, reject) => {
            axiosAPI
                .post(`/change_password`, {
                    password: password,
                    new_password: newPassword,
                })
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    ForgotPassword(email: string): Promise<{ message: string }> {
        return new Promise((resolve, reject) => {
            axios
                .post(`${apiAddress}/auth/forgot-password`, { email })
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    ResetPassword(code: string, newPassword: string): Promise<{ message: string }> {
        return new Promise((resolve, reject) => {
            axios
                .post(`${apiAddress}/auth/reset-password`, { code, new_password: newPassword })
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    Register(payload: {
        email: string;
        password: string;
        accept_platform_offer: boolean;
        accept_pd_consent: boolean;
    }): Promise<{ status: boolean; message: string }> {
        return new Promise((resolve, reject) => {
            axios
                .post(`${apiAddress}/auth/register`, payload)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    RegisterVerify(email: string, code: string): Promise<{ status: boolean; message: string }> {
        return new Promise((resolve, reject) => {
            axios
                .post(`${apiAddress}/auth/register/verify`, { email, code })
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    RegisterResend(email: string): Promise<{ status: boolean; message: string }> {
        return new Promise((resolve, reject) => {
            axios
                .post(`${apiAddress}/auth/register/resend`, { email })
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    AcceptLegal(documentTypes: string[]): Promise<{ status: boolean; message: string }> {
        return new Promise((resolve, reject) => {
            axiosAPI
                .post('/legal/accept', { document_types: documentTypes })
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    SendSupportMessage(subject: string, message: string, file: File | null): Promise<ResponseOK> {
        return new Promise((resolve, reject) => {
            const form = new FormData();
            form.append('subject', subject);
            form.append('message', message);
            if (file) {
                form.append('file', file);
            }
            axiosAPI
                .post('/support_message', form, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                })
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }
}

export const api = new API();
